import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageIcon, ChevronRightIcon, RotateCcwIcon, SparklesIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { PURCHASE_STATUS, isCurrent, type PurchaseOrder } from '../data/myOrders';
import { money } from '../data/food';
import { formatWhen } from '../utils/orderDates';
import { MyOrderDetail } from '../components/orders/MyOrderDetail';
type View = 'orders' | 'items';
type Tab = 'all' | 'current' | 'completed' | 'cancelled';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'current',
  label: 'Current'
}, {
  id: 'completed',
  label: 'Completed'
}, {
  id: 'cancelled',
  label: 'Cancelled'
}];
const inTab = (o: PurchaseOrder, tab: Tab) => tab === 'all' ? true : tab === 'current' ? isCurrent(o) : o.status === tab;

/** Everything the shopper has bought on Talab, across every seller. */
export function MyOrders() {
  const {
    orders,
    add
  } = useCart();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('orders');
  const [tab, setTab] = useState<Tab>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const current = orders.filter(isCurrent);
  const shown = orders.filter(o => inTab(o, tab));
  const openOrder = orders.find(o => o.id === openId) ?? null;

  /** Every item bought and received — one row per product, most recent first. */
  const purchased = useMemo(() => {
    const seen = new Map<string, {
      line: PurchaseOrder['lines'][number];
      order: PurchaseOrder;
      times: number;
    }>();
    orders.filter(o => o.status !== 'cancelled').forEach(o => o.lines.forEach(l => {
      const hit = seen.get(l.listingId);
      if (hit) hit.times += l.qty;else seen.set(l.listingId, {
        line: l,
        order: o,
        times: l.qty
      });
    }));
    return [...seen.values()];
  }, [orders]);
  return <div className="flex-1 p-4 lg:p-8 max-w-4xl mx-auto w-full" data-id="element-5809" data-mp="3ma84up9nq6c9">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6" data-id="element-5810" data-mp="3marpweultaui">
        <div data-id="element-5811" data-mp="3r7rwcqvd8fm5">
          <h1 className="font-display font-bold text-3xl text-dark-navy mb-1.5" data-id="element-5812" data-mp="3r89yg44plo1z">
            My Orders
          </h1>
          <p className="text-slate-500" data-id="element-5813" data-mp="3dbmon0rnr3en">
            {current.length > 0 ? `${current.length} on the way · everything you've bought on Talab` : "Everything you've bought on Talab, from every seller."}
          </p>
        </div>
        <div role="tablist" aria-label="Show" className="inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-5814" data-mp="38b8oe4wasxr6">
          {(['orders', 'items'] as View[]).map(v => <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-150 ease-out ${view === v ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-dark-navy'}`} data-id="element-5815" data-mp="2ylffjbnjiwqm">
              {v === 'orders' ? 'Orders' : 'Purchased items'}
            </button>)}
        </div>
      </div>

      {orders.length === 0 ? <Empty onBrowse={() => navigate('/')} data-id="element-5816" data-mp="2awqy8fag9bg8" /> : view === 'orders' ? <>
          <div className="flex gap-6 border-b border-slate-100 mb-5 overflow-x-auto" data-id="element-5817" data-mp="2ow052ocrmt7j">
            {TABS.map(t => {
          const n = orders.filter(o => inTab(o, t.id)).length;
          const active = tab === t.id;
          return <button key={t.id} onClick={() => setTab(t.id)} className={`pb-3 -mb-px border-b-2 text-sm whitespace-nowrap transition-colors duration-150 ease-out ${active ? 'border-primary-indigo text-dark-navy font-semibold' : 'border-transparent text-slate-500 hover:text-dark-navy'}`} data-id="element-5818" data-mp="2yonxl6wpqb0g">
                  {t.label}
                  <span className="ml-1.5 text-slate-400 font-normal" data-id="element-5819" data-mp="15wj2hkapwvjh">{n}</span>
                </button>;
        })}
          </div>

          {shown.length === 0 ? <p className="py-16 text-center text-sm text-slate-500" data-id="element-5820" data-mp="1fmvlh82vwgni">
              No {tab === 'current' ? 'current' : tab} orders.
            </p> : <ul className="space-y-3" data-id="element-5821" data-mp="myzn7yxo2gom">
              {shown.map(o => <li key={o.id} data-id="element-5822" data-mp="n12pbhc3g09p">
                  <OrderRow order={o} onOpen={() => setOpenId(o.id)} data-id="element-5823" data-mp="rxt2nwwikhrf" />
                </li>)}
            </ul>}
        </> : purchased.length === 0 ? <p className="py-16 text-center text-sm text-slate-500" data-id="element-5824" data-mp="23dhqsrli7mtt">
          Items you buy will appear here.
        </p> : <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5825" data-mp="27ebcdebh4pjj">
          {purchased.map(({
        line,
        order,
        times
      }) => <li key={line.listingId} className="flex gap-3 p-3 border border-slate-200 rounded-2xl" data-id="element-5826" data-mp="3rwlx3bcmannc">
              <img src={line.image} alt="" className="w-20 h-20 rounded-xl object-cover bg-slate-100 flex-shrink-0" data-id="element-5827" data-mp="3j4tpd8ez9smg" />
              <div className="flex-1 min-w-0 flex flex-col" data-id="element-5828" data-mp="3e3midbknsxgu">
                <p className="text-sm font-semibold text-dark-navy leading-snug truncate" data-id="element-5829" data-mp="397p55e4p078a">
                  {line.title}
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5 truncate" data-id="element-5830" data-mp="349vykjzh9b7h">
                  {order.merchant} · {formatWhen(order.placedAt)}
                  {times > 1 ? ` · bought ${times}×` : ''}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-2" data-id="element-5831" data-mp="yeb45vj89jc5">
                  <span className="text-sm font-semibold text-dark-navy" data-id="element-5832" data-mp="tfhzmslx52o4">
                    {money(line.unitPrice)}
                  </span>
                  <div className="flex items-center gap-3" data-id="element-5833" data-mp="1cj1wmkf08p3s">
                    <button onClick={() => setOpenId(order.id)} className="text-[13px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-5834" data-mp="1hatiihn7u6nl">
                      Order
                    </button>
                    <button onClick={() => add({
                ...line,
                qty: 1,
                merchant: order.merchant
              })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-indigo/10 text-primary-indigo text-[13px] font-semibold hover:bg-primary-indigo/15 transition-colors duration-150 ease-out" data-id="element-5835" data-mp="1w8dfp2dvexj7">
                      <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-5836" data-mp="au7zf7e7h7t6" />
                      Buy again
                    </button>
                  </div>
                </div>
              </div>
            </li>)}
        </ul>}

      <MyOrderDetail order={openOrder} onClose={() => setOpenId(null)} data-id="element-5837" data-mp="89057o008oab" />
    </div>;
}
function OrderRow({
  order,
  onOpen
}: {
  order: PurchaseOrder;
  onOpen: () => void;
}) {
  const status = PURCHASE_STATUS[order.status];
  const itemCount = order.lines.reduce((n, l) => n + l.qty, 0);
  const first = order.lines[0];
  const more = order.lines.length - 1;
  return <button onClick={onOpen} className="w-full text-left flex items-center gap-4 p-4 border border-slate-200 rounded-2xl hover:border-slate-300 hover:bg-slate-50/60 transition-colors duration-150 ease-out" data-id="element-5838" data-mp="3kqyfd1ksawe0">
      <div className="relative flex-shrink-0" data-id="element-5839" data-mp="3uib6ts2o7kji">
        <img src={first.image} alt="" className={`w-16 h-16 rounded-xl object-cover bg-slate-100 ${order.status === 'cancelled' ? 'opacity-60' : ''}`} data-id="element-5840" data-mp="3pqt87ao4dw1p" />
        {more > 0 && <span className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-md bg-dark-navy text-white text-[10px] font-bold" data-id="element-5841" data-mp="2x2oxubulsuso">
            +{more}
          </span>}
      </div>

      <div className="flex-1 min-w-0" data-id="element-5842" data-mp="2ghx9gw57yj34">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-5843" data-mp="2giox9kskrciu">
          <p className="font-semibold text-dark-navy truncate" data-id="element-5844" data-mp="2lh9zfpre069x">
            {order.merchant}
          </p>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${status.tone}`} data-id="element-5845" data-mp="2qgmkc4yp739v">
            {status.label}
          </span>
        </div>
        <p className="text-sm text-slate-600 truncate mt-0.5" data-id="element-5846" data-mp="2zgjfmpflf0dw">
          {first.title}
          {more > 0 ? ` and ${more} more` : ''}
        </p>
        <p className="text-[12px] text-slate-500 mt-0.5 truncate" data-id="element-5847" data-mp="3481en7y22qw8">
          {isCurrent(order) ? order.fulfilment : `${formatWhen(order.placedAt)} · ${itemCount} item${itemCount === 1 ? '' : 's'} · ${order.id}`}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-5848" data-mp="3j490ecp440dm">
        <span className="font-semibold text-dark-navy" data-id="element-5849" data-mp="3j2o0s8nxxr8w">
          {money(order.total)}
        </span>
        <ChevronRightIcon className="w-4 h-4 text-slate-300" data-id="element-5850" data-mp="17f2h7mxvfu2u" />
      </div>
    </button>;
}
function Empty({
  onBrowse
}: {
  onBrowse: () => void;
}) {
  return <div className="py-20 flex flex-col items-center text-center" data-id="element-5851" data-mp="xk0t8ufc0y1u">
      <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center" data-id="element-5852" data-mp="sko5m7gmzmy7">
        <PackageIcon className="w-6 h-6 text-slate-300" data-id="element-5853" data-mp="1q7n66tag9f1d" />
      </div>
      <h2 className="font-display font-bold text-lg text-dark-navy mt-4" data-id="element-5854" data-mp="1q9zj2s1coz7k">
        No orders yet
      </h2>
      <p className="text-sm text-slate-500 mt-1.5 max-w-xs" data-id="element-5855" data-mp="1lb67x69jdkak">
        When you order through Talab, you can follow it and find it again here.
      </p>
      <button onClick={onBrowse} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-5856" data-mp="1bhz77ypqqsgt">
        <SparklesIcon className="w-4 h-4" data-id="element-5857" data-mp="245l8h0bhxl0i" />
        Ask Talab for something
      </button>
    </div>;
}